import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoCartOutline, IoCheckmark } from 'react-icons/io5';
import { ReactComponent as HeartImg } from '../../Components/images/assets/heart.svg';
import newTag from '../../Components/images/assets/new.svg';
import axios from 'axios';
import { useProfile } from '../../Contexts/ProfileContext';
import { useToast } from '../../Contexts/ToastContext';
import './SimpleTouchCarouselCard.scss';

const AUTO_ADVANCE_MS = 2200;
const VIEWPORT_TAP_SLOP_PX = 12;

function getGuestCartProductIds() {
    try {
        const raw = localStorage.getItem('guestCart');
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return (arr || []).map((i) => String(i.product_id));
    } catch {
        return [];
    }
}

/**
 * Mobile product card: tap / click → PDP. Multi-image + inactive: finger moves beyond tap slop on the
 * image in any direction starts the carousel.
 * @param {boolean} [clickStartsCarouselForTesting] — if true, a click on the image (multi-image, inactive)
 *   activates the carousel instead of navigating (desktop DevTools testing only; remove for production).
 */
function SimpleTouchCarouselCardComponent({
    product,
    isActive: controlledActive,
    onActivate,
    gridIndex,
    clickStartsCarouselForTesting = false,
}) {
    const navigate = useNavigate();
    const { fetchProfile } = useProfile();
    const { showToast } = useToast();
    const { id, urls: images = [], title, price, brand, mrp, rating, reviewCount } = product;
    const firstImage = images[0];

    const [localActive, setLocalActive] = useState(false);
    const isActive = typeof controlledActive === 'boolean' ? controlledActive : localActive;
    const [slideIndex, setSlideIndex] = useState(0);
    const touchActivatedRef = useRef(false);
    const viewportTouchRef = useRef(null);

    const [addingToCart, setAddingToCart] = useState(null);
    const [addedToCart, setAddedToCart] = useState(() => {
        const isGuest = typeof localStorage !== 'undefined' && localStorage.getItem('guestUser') === 'true';
        if (isGuest && getGuestCartProductIds().includes(String(id))) return String(id);
        return null;
    });
    const [addingToWishlist, setAddingToWishlist] = useState(null);
    const [addedToWishlist, setAddedToWishlist] = useState(null);

    const canCarousel = images.length > 1;

    const listImageLoading =
        typeof gridIndex === 'number' ? (gridIndex < 4 ? 'eager' : 'lazy') : 'eager';

    useEffect(() => {
        if (!isActive || !canCarousel) return;
        const t = window.setInterval(() => {
            setSlideIndex((i) => (i + 1) % images.length);
        }, AUTO_ADVANCE_MS);
        return () => window.clearInterval(t);
    }, [isActive, canCarousel, images.length]);

    useEffect(() => {
        if (!isActive) {
            setSlideIndex(0);
            touchActivatedRef.current = false;
        }
    }, [isActive]);

    const handleActivate = useCallback(() => {
        touchActivatedRef.current = true;
        if (onActivate) onActivate(id);
        else setLocalActive(true);
    }, [id, onActivate]);

    useEffect(() => {
        const el = viewportTouchRef.current;
        if (!el || isActive || !canCarousel) return;

        let sx = 0;
        let sy = 0;
        let maxExcursion = 0;
        let carouselStarted = false;

        const onTouchStart = (e) => {
            if (e.touches.length !== 1) return;
            sx = e.touches[0].clientX;
            sy = e.touches[0].clientY;
            maxExcursion = 0;
            carouselStarted = false;
        };

        const onTouchMove = (e) => {
            if (e.touches.length !== 1) return;
            const x = e.touches[0].clientX;
            const y = e.touches[0].clientY;
            const d = Math.hypot(x - sx, y - sy);
            if (d > maxExcursion) maxExcursion = d;
            if (!carouselStarted && maxExcursion > VIEWPORT_TAP_SLOP_PX) {
                carouselStarted = true;
                handleActivate();
            }
        };

        const onTouchEnd = (e) => {
            if (e.changedTouches.length !== 1) return;
            const t = e.changedTouches[0];
            const endDist = Math.hypot(t.clientX - sx, t.clientY - sy);
            const furthest = Math.max(maxExcursion, endDist);
            if (!carouselStarted && furthest > VIEWPORT_TAP_SLOP_PX) {
                carouselStarted = true;
                handleActivate();
            }
            if (carouselStarted) {
                e.preventDefault();
            }
        };

        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: true });
        el.addEventListener('touchend', onTouchEnd, { passive: false });

        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, [isActive, canCarousel, handleActivate]);

    useEffect(() => {
        const isGuest = typeof localStorage !== 'undefined' && localStorage.getItem('guestUser') === 'true';
        if (!isGuest) return;
        const check = () => {
            if (getGuestCartProductIds().includes(String(id))) setAddedToCart(String(id));
        };
        check();
        window.addEventListener('guestCartUpdated', check);
        return () => window.removeEventListener('guestCartUpdated', check);
    }, [id]);

    const onImageClick = useCallback(() => {
        navigate(`/specific/${id}`);
    }, [id, navigate]);

    const handleViewportClick = useCallback(
        (e) => {
            if (clickStartsCarouselForTesting && canCarousel && !isActive) {
                e.preventDefault();
                e.stopPropagation();
                handleActivate();
                return;
            }
            onImageClick();
        },
        [clickStartsCarouselForTesting, canCarousel, isActive, handleActivate, onImageClick]
    );

    const onMetaAreaClick = useCallback(
        (e) => {
            if (e.target.closest('button')) return;
            navigate(`/specific/${id}`);
        },
        [id, navigate]
    );

    const handleAddToCart = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (addedToCart === String(id)) {
            showToast({ message: 'Already in cart' });
            return;
        }
        setAddingToCart(id);
        try {
            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (isGuest) {
                const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
                const existingIndex = guestCart.findIndex((item) => String(item.product_id) === String(id));
                if (existingIndex >= 0) {
                    guestCart[existingIndex].selectedQty = (guestCart[existingIndex].selectedQty || 1) + 1;
                } else {
                    guestCart.push({
                        _id: `guest-cart-${Date.now()}-${id}`,
                        product_id: id,
                        urls: images,
                        title: title || 'Product',
                        price,
                        brand,
                        mrp,
                        selectedSize: 'M',
                        selectedQty: 1,
                        size: { M: 10 },
                        availableSizes: ['M'],
                        availableQuantities: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
                        maxStock: 10,
                    });
                }
                localStorage.setItem('guestCart', JSON.stringify(guestCart));
                setAddedToCart(String(id));
                window.dispatchEvent(new Event('guestCartUpdated'));
                window.dispatchEvent(new Event('cartBadgePulse'));
                showToast({
                    message: 'Added to cart',
                    action: { label: 'View cart', onClick: () => navigate('/cart') },
                });
            } else {
                const token = localStorage.getItem('token');
                if (!token) {
                    showToast({
                        message: 'Please login to add items to cart',
                        variant: 'info',
                        action: { label: 'Login', onClick: () => navigate('/auth/login') },
                    });
                    return;
                }
                const pincode = '110001';
                await axios.post(
                    `/cart/${id}`,
                    { selectedSize: 'M', selectedQty: 1, pincode },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setAddedToCart(String(id));
                await fetchProfile();
                window.dispatchEvent(new Event('cartBadgePulse'));
                showToast({
                    message: 'Added to cart',
                    action: { label: 'View cart', onClick: () => navigate('/cart') },
                });
            }
        } catch (err) {
            console.error(err);
            showToast({ message: err.response?.data?.message || 'Could not add to cart. Try again.' });
        } finally {
            setAddingToCart(null);
        }
    };

    const handleAddToWishlist = async (e) => {
        e.stopPropagation();

        if (addedToWishlist === String(id)) {
            showToast({
                message: 'Already in wishlist',
                action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
            });
            return;
        }

        setAddingToWishlist(id);
        try {
            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (isGuest) {
                const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
                const exists = guestWishlist.some((item) => String(item.product_id) === String(id));
                if (!exists) {
                    guestWishlist.push({
                        _id: `guest-wish-${Date.now()}-${id}`,
                        product_id: id,
                        urls: images || [],
                        title: title || 'Product',
                        price,
                        brand,
                        mrp,
                        selectedSize: 'M',
                    });
                    localStorage.setItem('guestWishlist', JSON.stringify(guestWishlist));
                }
                setAddedToWishlist(String(id));
                setTimeout(() => setAddedToWishlist(null), 3000);
                showToast({
                    message: 'Added to wishlist',
                    action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
                });
                return;
            }

            const token = localStorage.getItem('token');
            if (!token) {
                setAddingToWishlist(null);
                showToast({ message: 'Please login to add items to wishlist' });
                return;
            }

            await axios.post(`/products/wish-list/${id}`, { selectedSize: 'M' }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setAddedToWishlist(String(id));
            setTimeout(() => setAddedToWishlist(null), 3000);
            await fetchProfile();
            showToast({
                message: 'Added to wishlist',
                action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
            });
        } catch (error) {
            if (error.response?.status === 400) {
                setAddedToWishlist(String(id));
                setTimeout(() => setAddedToWishlist(null), 3000);
                showToast({
                    message: 'Already in wishlist',
                    action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
                });
            } else {
                showToast({ message: 'Could not add to wishlist. Try again.' });
            }
        } finally {
            setAddingToWishlist(null);
        }
    };

    const discount = mrp != null && price != null && mrp > 0 ? Math.round((1 - price / mrp) * 100) : 0;
    const ratingValue = rating != null ? rating : 4.2;
    const ratingCountText =
        reviewCount != null
            ? reviewCount >= 1000
                ? `${(reviewCount / 1000).toFixed(1)}K`
                : reviewCount
            : '4.2K';

    const displayIndex = isActive ? slideIndex % images.length : 0;
    const src = images[displayIndex] || '';

    return (
        <article
            className={`simple-touch-card ${isActive ? 'simple-touch-card--active' : 'simple-touch-card--inactive'}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/specific/${id}`)}
        >
            <div
                ref={viewportTouchRef}
                className="simple-touch-card__viewport"
                onClick={handleViewportClick}
                role={canCarousel ? 'presentation' : undefined}
            >
                {src ? (
                    <img
                        className="simple-touch-card__img"
                        src={src}
                        alt=""
                        loading={listImageLoading}
                        decoding="async"
                    />
                ) : null}

                {firstImage ? (
                    <div className="simple-touch-card__new-tag" aria-hidden>
                        <img src={newTag} alt="New" />
                    </div>
                ) : null}

                {firstImage && !isActive ? (
                    <div className="simple-touch-card__rating">
                        <span>{ratingValue} *</span>
                        <span>| {ratingCountText}</span>
                    </div>
                ) : null}

                {/* {canCarousel && !isActive ? (
                    <div className="simple-touch-card__hint" aria-hidden>
                        Touch image to play
                    </div>
                ) : null} */}

                {isActive && canCarousel ? (
                    <div className="simple-touch-card__dots-wrap" aria-hidden>
                        <ul className="simple-touch-card__dots">
                            {images.map((_, i) => (
                                <li key={i} className={displayIndex === i ? 'active' : ''}>
                                    <span />
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}
            </div>

            <div className="simple-touch-card__meta" onClick={onMetaAreaClick} role="presentation">
                <div className="simple-touch-card__meta-row">
                    <div className="simple-touch-card__brand">{brand ?? '—'}</div>
                    {isActive ? (
                        <div className="simple-touch-card__actions-inline">
                            <button
                                type="button"
                                className={`action-cart ${addingToCart ? 'loading' : ''} ${addedToCart ? 'added' : ''}`}
                                onClick={handleAddToCart}
                                aria-label="Add to cart"
                            >
                                {addingToCart ? (
                                    <span className="spinner" />
                                ) : addedToCart ? (
                                    <IoCheckmark size="1.25em" />
                                ) : (
                                    <IoCartOutline size="1.25em" />
                                )}
                            </button>
                            <button
                                type="button"
                                className={`action-wishlist ${addingToWishlist ? 'loading' : ''} ${addedToWishlist ? 'added' : ''}`}
                                onClick={handleAddToWishlist}
                                aria-label="Add to wishlist"
                            >
                                {addingToWishlist ? (
                                    <span className="spinner" />
                                ) : addedToWishlist ? (
                                    <IoCheckmark size="1.25em" />
                                ) : (
                                    <HeartImg width="1.25em" height="1.25em" />
                                )}
                            </button>
                        </div>
                    ) : null}
                </div>
                {title ? <div className="simple-touch-card__title">{title}</div> : null}
                <div className="simple-touch-card__offer-details">
                    {price != null && <span>Rs. {price}</span>}
                    {mrp != null && mrp > 0 && <span>Rs. {mrp}</span>}
                    {discount > 0 && <span>({discount}% OFF)</span>}
                </div>
            </div>
        </article>
    );
}

export const SimpleTouchCarouselCard = memo(SimpleTouchCarouselCardComponent);
